import React from 'react';
import { cn } from '../../lib/utils';
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import type { SortConfig, SortField, SortOrder, PaginationConfig } from '../../lib/types';

interface Props {
  sort: SortConfig;
  onSortChange: (field: SortField, order: SortOrder) => void;
  pagination: PaginationConfig;
  onPageChange: (page: number) => void;
  className?: string;
}

export function MovieListControls({
  sort,
  onSortChange,
  pagination,
  onPageChange,
  className
}: Props) {
  const totalPages = Math.ceil(pagination.total / pagination.perPage);
  const showPagination = pagination.total > pagination.perPage;

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const [field, order] = event.target.value.split('-') as [SortField, SortOrder];
    onSortChange(field, order);
  };

  return (
    <div className={cn('flex flex-col items-center justify-between gap-4 py-2 sm:flex-row', className)}>
      <div className="flex items-center gap-2.5">
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
        <select
          value={`${sort.field}-${sort.order}`}
          onChange={handleSortChange}
          className={cn(
            'rounded-lg border border-white/10 bg-input py-1.5 pl-3 pr-8 text-sm text-foreground',
            'focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/25'
          )}
        >
          <option value="title-asc">Title (A-Z)</option>
          <option value="title-desc">Title (Z-A)</option>
          <option value="added-desc">Recently Added</option>
          <option value="added-asc">Oldest Added</option>
          <option value="year-desc">Newest Movies</option>
          <option value="year-asc">Oldest Movies</option>
        </select>
      </div>

      {showPagination && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            aria-label="Previous page"
            className={cn(
              'rounded-lg border border-white/10 p-1.5 text-muted-foreground',
              'transition-colors hover:border-white/25 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-40'
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <span className="text-sm text-muted-foreground">
            Page {pagination.page} of {totalPages}
          </span>

          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page === totalPages}
            aria-label="Next page"
            className={cn(
              'rounded-lg border border-white/10 p-1.5 text-muted-foreground',
              'transition-colors hover:border-white/25 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-40'
            )}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
